/* ==========================================================================
   AUMI — product-rail.js
   <product-recommendations>: busca as recomendações via Section Rendering
   (routes.product_recommendations_url) quando a seção se aproxima da tela.
   <recently-viewed>: lê os handles guardados pelo product-page.js no
   localStorage e renderiza cada cartão com /products/<handle>?section_id=recently-viewed.
   ========================================================================== */
(() => {
  if (window.__aumiProductRail) return;
  window.__aumiProductRail = true;

  const RECENT_KEY = 'aumi:recently-viewed';

  const readRecent = () => {
    try {
      return JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
    } catch (e) {
      return [];
    }
  };

  const onNear = (el, callback) => {
    if (!('IntersectionObserver' in window)) return callback();
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          observer.disconnect();
          callback();
        }
      },
      { rootMargin: '0px 0px 600px 0px' }
    );
    observer.observe(el);
    return observer;
  };

  /* setas do carrossel (só aparecem quando há rolagem) */
  const setupNav = (root) => {
    const track = root.querySelector('[data-rail-track]');
    const nav = root.querySelector('[data-rail-nav]');
    if (!track || !nav) return;
    const prev = nav.querySelector('[data-rail-prev]');
    const next = nav.querySelector('[data-rail-next]');
    const update = () => {
      const scrollable = track.scrollWidth > track.clientWidth + 4;
      nav.hidden = !scrollable;
      if (!scrollable) return;
      prev.disabled = track.scrollLeft <= 4;
      next.disabled = track.scrollLeft + track.clientWidth >= track.scrollWidth - 4;
    };
    const step = () => {
      const item = track.firstElementChild;
      return item ? item.getBoundingClientRect().width + 16 : track.clientWidth * 0.8;
    };
    prev.onclick = () => track.scrollBy({ left: -step(), behavior: 'smooth' });
    next.onclick = () => track.scrollBy({ left: step(), behavior: 'smooth' });
    track.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update, { passive: true });
    update();
  };

  const revealSection = (el, show) => {
    const section = el.closest('[data-rail-section]');
    if (section) section.hidden = !show;
  };

  class ProductRecommendations extends HTMLElement {
    connectedCallback() {
      if (this.querySelector('[data-rail-item]')) {
        setupNav(this);
        return;
      }
      if (!this.dataset.url) return;
      this.observer = onNear(this, () => this.load());
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    async load() {
      try {
        const response = await fetch(this.dataset.url);
        const html = await response.text();
        const doc = new DOMParser().parseFromString(html, 'text/html');
        const fresh = doc.querySelector('product-recommendations');
        if (fresh && fresh.querySelector('[data-rail-item]')) {
          this.innerHTML = fresh.innerHTML;
          setupNav(this);
          revealSection(this, true);
        } else if (!window.Shopify || !window.Shopify.designMode) {
          revealSection(this, false);
        }
      } catch (e) {
        revealSection(this, false);
      }
    }
  }

  class RecentlyViewed extends HTMLElement {
    connectedCallback() {
      const current = this.dataset.currentHandle;
      const limit = Number(this.dataset.limit) || 4;
      this.handles = readRecent()
        .filter((handle) => handle && handle !== current)
        .slice(0, limit);
      if (!this.handles.length) return;
      this.observer = onNear(this, () => this.load());
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    async load() {
      const root = ((window.AUMI && window.AUMI.routes && window.AUMI.routes.root) || '/').replace(/\/$/, '');
      const section = this.dataset.renderSection || 'recently-viewed';
      const cards = await Promise.all(
        this.handles.map((handle) =>
          fetch(`${root}/products/${encodeURIComponent(handle)}?section_id=${section}`)
            .then((response) => (response.ok ? response.text() : ''))
            .then((html) => {
              if (!html) return '';
              const doc = new DOMParser().parseFromString(html, 'text/html');
              const template = doc.querySelector('template[data-rv-card]');
              return template ? template.innerHTML.trim() : '';
            })
            .catch(() => '')
        )
      );
      const items = cards.filter(Boolean);
      if (!items.length) return;
      const track = this.querySelector('[data-rail-track]');
      track.innerHTML = items.map((card) => `<li class="product-rail__item">${card}</li>`).join('');
      const empty = this.querySelector('[data-rail-empty]');
      if (empty) empty.hidden = true;
      revealSection(this, true);
      setupNav(this);
    }
  }

  if (!customElements.get('product-recommendations')) customElements.define('product-recommendations', ProductRecommendations);
  if (!customElements.get('recently-viewed')) customElements.define('recently-viewed', RecentlyViewed);
})();
